import type { AuthSession, SpaAuth } from "@coten-inc/coten-auth-spa";
import React from "react";

import type { User } from "~/common/types";
import { authorizeConnect, clearConnect, getConnectAuthenticatedFetch } from "~/features/authn/connectAuth";
import { createDeweyAuth } from "~/features/authn/cotenAuth";
import { useUser } from "~/features/authn/hooks";
import { loadAuthenticatedUser, UnregisteredUserError } from "~/features/authn/loadAuthenticatedUser";
import type { AuthenticatedFetch } from "~/lib/apolloClient";

export interface AuthContextInterface {
  user: User | null;
  authenticatedFetch: AuthenticatedFetch;
  signIn: () => Promise<void>;
  signOut: () => void;
  signInErrorMessage: string | null;
}

const authenticationUnavailable = "Authentication is temporarily unavailable";

export const AuthContext = React.createContext<AuthContextInterface>({
  user: null,
  authenticatedFetch: () => Promise.reject(new Error(authenticationUnavailable)),
  signIn: () => Promise.reject(new Error(authenticationUnavailable)),
  signOut: () => {},
  signInErrorMessage: null,
});

export const useAuth = () => React.useContext(AuthContext);

interface AuthProviderProps {
  loadingComponent: React.ReactNode;
  children: React.ReactNode;
}

const sessionErrorMessage = (session: AuthSession): string | null => {
  if (session.status === "authenticated" || session.reason === "missing") {
    return null;
  }
  if (session.reason === "cancelled") {
    return "Sign-in was cancelled";
  }
  return "Sign-in could not be completed";
};

export const AuthProvider = ({ loadingComponent, children }: AuthProviderProps) => {
  const [auth, setAuth] = React.useState<SpaAuth | null>(null);
  const [cotenSession, setCotenSession] = React.useState<AuthSession | null>(null);
  const [cotenReady, setCotenReady] = React.useState(false);
  const [cotenInitializationVersion, setCotenInitializationVersion] = React.useState(0);
  const [user, setUser] = useUser();
  const [loading, setLoading] = React.useState(true);
  const [sessionSignInErrorMessage, setSessionSignInErrorMessage] = React.useState<string | null>(null);
  const [userLoadErrorMessage, setUserLoadErrorMessage] = React.useState<string | null>(null);
  const pendingCotenSignInRef = React.useRef<string | null>(null);
  const signInErrorMessage = userLoadErrorMessage ?? sessionSignInErrorMessage;

  React.useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    void Promise.resolve()
      .then(() => createDeweyAuth())
      .then((instance) => {
        if (!active) return;
        setAuth(instance);
        const applySession = (session: AuthSession) => {
          if (!active) return;
          setCotenSession(session);
          setSessionSignInErrorMessage(sessionErrorMessage(session));
        };
        unsubscribe = instance.subscribe(applySession);
        applySession(instance.getSession());
        const returnTo = pendingCotenSignInRef.current;
        if (returnTo) {
          pendingCotenSignInRef.current = null;
          void instance.signIn({ returnTo }).catch(() => {
            if (active) setSessionSignInErrorMessage("Sign-in could not be started");
          });
        }
      })
      .catch(() => {
        if (active) {
          setAuth(null);
          setCotenSession(null);
          setSessionSignInErrorMessage(authenticationUnavailable);
        }
      })
      .finally(() => {
        if (active) setCotenReady(true);
      });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [cotenInitializationVersion]);

  const authenticatedFetch = React.useMemo<AuthenticatedFetch | null>(
    () =>
      auth && cotenSession?.status === "authenticated"
        ? getConnectAuthenticatedFetch((input, init) => auth.fetch(input, init))
        : null,
    [auth, cotenSession],
  );

  React.useEffect(() => {
    if (!cotenReady) return;

    let active = true;
    if (!authenticatedFetch) {
      setUser(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    void authorizeConnect()
      .then(async (ready) => {
        if (!active || !ready) return;
        const authenticatedUser = await loadAuthenticatedUser(authenticatedFetch);
        if (active) {
          setUser(authenticatedUser);
          setUserLoadErrorMessage(null);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setUser(null);
          setUserLoadErrorMessage(
            error instanceof UnregisteredUserError ? "User is not registered" : "User information could not be loaded",
          );
          if (error instanceof UnregisteredUserError) {
            clearConnect();
            void Promise.resolve()
              .then(() => auth?.signOut())
              .catch(() => undefined);
          }
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [auth, authenticatedFetch, cotenReady, setUser]);

  const signIn = React.useCallback(async (): Promise<void> => {
    setUserLoadErrorMessage(null);
    if (!auth) {
      pendingCotenSignInRef.current = window.location.href;
      setCotenReady(false);
      setCotenInitializationVersion((version) => version + 1);
      return;
    }
    setSessionSignInErrorMessage(null);
    try {
      if (auth.getSession().status === "authenticated") {
        try {
          if (!(await authorizeConnect(true))) return;
        } catch {
          clearConnect();
        }
      }
      await auth.signIn({ returnTo: window.location.href });
    } catch {
      setSessionSignInErrorMessage("Sign-in could not be started");
    }
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authenticatedFetch: authenticatedFetch ?? (() => Promise.reject(new Error(authenticationUnavailable))),
        signIn,
        signOut: () => {
          clearConnect();
          void auth?.signOut();
        },
        signInErrorMessage,
      }}
    >
      {loading ? loadingComponent : children}
    </AuthContext.Provider>
  );
};
